import { fetchSuggestedPrompts } from "@lib/chatApi";
import { useChatStore } from "@lib/stores/chatStore";
import type { ChatAttachment, ChatRichTextArtefact } from "@lib/types";
import { useEffect, useState } from "react";
import { ChatInput } from "./ChatInput";
import { PlanModeToggle } from "./PlanModeToggle";

interface ChatComposerViewProps {
  onSend: (
    message: string,
    attachments: ChatAttachment[],
    richTextArtefacts: ChatRichTextArtefact[],
    options: { planModeEnabled: boolean },
  ) => void;
  disabled?: boolean;
}

function suggestedPromptsFor(campaignId?: string): string[] {
  if (campaignId) {
    return [
      "Summarise the pipeline for this role",
      "Shortlist the top candidates",
      "Assess a candidate against this role",
      "Draft outreach for a candidate",
    ];
  }
  return [
    "Find candidates for a role",
    "Summarise activity across my campaigns",
    "Draft a job description",
    "What should I work on next?",
  ];
}

export function ChatComposerView({ onSend, disabled }: ChatComposerViewProps) {
  const viewContext = useChatStore((s) => s.viewContext);
  const [draft, setDraft] = useState<string | undefined>(undefined);
  const [planModeEnabled, setPlanModeEnabled] = useState(false);
  // Seed with the static defaults so there's no empty flash, then replace
  // with the dynamic set (lead prompt = the campaign's recommended next
  // action when a Decision Summary exists). Falls back to the seed on error.
  const campaignId = viewContext.campaign_id;
  const [prompts, setPrompts] = useState<string[]>(() => suggestedPromptsFor(campaignId));

  useEffect(() => {
    let active = true;
    setPrompts(suggestedPromptsFor(campaignId));
    fetchSuggestedPrompts(campaignId)
      .then((dynamicPrompts) => {
        if (active && dynamicPrompts.length > 0) setPrompts(dynamicPrompts);
      })
      .catch(() => {
        // Keep the static defaults already seeded above.
      });
    return () => {
      active = false;
    };
  }, [campaignId]);

  function handleSend(
    message: string,
    attachments: ChatAttachment[],
    richTextArtefacts: ChatRichTextArtefact[],
  ) {
    onSend(message, attachments, richTextArtefacts, { planModeEnabled });
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto px-6 py-10 flex flex-col items-center justify-center text-center">
        <h3 className="text-lg font-semibold text-text-primary mb-1">How can I help today?</h3>
        <p className="text-sm text-text-tertiary mb-6 max-w-xs">
          Start a new conversation, or pick a prompt to begin.
        </p>
        <div className="w-full max-w-sm space-y-2">
          {prompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => setDraft(prompt)}
              className="w-full text-left text-sm rounded-md border border-border px-3 py-2 text-text-primary hover:bg-surface-secondary transition-colors"
            >
              {prompt}
            </button>
          ))}
        </div>
      </div>
      <ChatInput
        onSend={handleSend}
        disabled={disabled}
        placeholder="Message"
        initialValue={draft}
        trailingControl={
          <PlanModeToggle
            enabled={planModeEnabled}
            onChange={({ enabled }) => setPlanModeEnabled(enabled)}
          />
        }
      />
    </div>
  );
}
